import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Gift, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * Reusable admin tool — gift Bula Pro (or revoke) to one or many users by
 * email. Wraps `/api/admin/comp-pro` and `/api/admin/revoke-comp-pro`.
 *
 * Used on:
 *   - /admin (full operator dashboard)
 *   - /dashboard (top-of-page admin quick-action)
 *
 * The endpoint itself enforces admin-only; this component just renders
 * the form. Parent decides when/if to mount it.
 */
export default function CompProPanel() {
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const submit = async (mode) => {
    const list = emails.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one email"); return; }
    setBusy(true);
    try {
      const url = mode === "grant" ? "/admin/comp-pro" : "/admin/revoke-comp-pro";
      const { data } = await api.post(url, { emails: list });
      setLastResult({ mode, ...data });
      if (mode === "grant") {
        toast.success(`Granted Pro to ${data.granted.length} user(s)${data.missing.length ? ` · ${data.missing.length} not found` : ""}`);
      } else {
        toast.success(`Revoked Pro from ${data.revoked?.length || 0} user(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="card-default p-5 space-y-3" data-testid="admin-comp-pro-panel">
      <div className="flex items-center gap-2">
        <Gift size={16} weight="fill" className="text-[#E05D3A]" />
        <span className="label-eyebrow">Comp Pro</span>
      </div>
      <h2 className="font-heading font-bold text-xl">Gift Bula Pro to a player</h2>
      <p className="text-xs text-[#0F2537]/70">
        Grant permanent Pro to one or many emails &mdash; pasted comma-, space-, or newline-separated.
        Plan is tagged <code className="bg-[#0F2537]/5 px-1 rounded">comp</code> so it doesn&rsquo;t pollute paying-subscriber metrics.
      </p>
      <textarea
        value={emails}
        onChange={(e) => setEmails(e.target.value)}
        placeholder="dwilliamrock@yahoo.com, friend@example.com"
        rows={3}
        data-testid="admin-comp-emails"
        className="w-full px-3 py-2 border border-[#E2E0D8] rounded-lg text-sm font-mono focus:outline-none focus:border-[#0F2537]"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="admin-comp-grant-btn"
          disabled={busy}
          onClick={() => submit("grant")}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-bold hover:brightness-110 transition disabled:opacity-60"
        >
          <PaperPlaneTilt size={14} weight="fill" /> Grant Pro
        </button>
        <button
          type="button"
          data-testid="admin-comp-revoke-btn"
          disabled={busy}
          onClick={() => submit("revoke")}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-[#E2E0D8] text-[#0F2537] text-sm font-bold hover:border-[#0F2537] transition disabled:opacity-60"
        >
          Revoke comp
        </button>
      </div>
      {lastResult && (
        <div className="text-xs font-mono bg-[#0F2537]/5 p-3 rounded-lg" data-testid="admin-comp-result">
          {lastResult.mode === "grant" && (
            <>
              <div className="text-[#1E5631] font-bold">Granted ({lastResult.granted.length}): {lastResult.granted.join(", ") || "—"}</div>
              {lastResult.missing.length > 0 && (
                <div className="text-[#E05D3A] mt-1">Email not found ({lastResult.missing.length}): {lastResult.missing.join(", ")}</div>
              )}
              <div className="text-[#0F2537]/60 mt-1">Expires: {lastResult.expires_at}</div>
            </>
          )}
          {lastResult.mode === "revoke" && (
            <div className="text-[#1E5631] font-bold">Revoked ({lastResult.revoked?.length || 0}): {(lastResult.revoked || []).join(", ") || "—"}</div>
          )}
        </div>
      )}
    </section>
  );
}
